import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ShieldCheck, ShieldOff, KeyRound, X } from "lucide-react";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import MfaSetupDialog from "../MfaSetupDialog";
import { LoadError, LoadingState } from "../Settings/LoadState";
import { tokens } from "../Triage/styles";

const themeTokens = (theme) => tokens(theme.palette.type === "dark");

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2.5),
    borderRadius: 12,
    background: themeTokens(theme).surface,
    border: `1px solid ${themeTokens(theme).border}`,
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  left: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    minWidth: 0,
  },
  badge: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  badgeOn: {
    background: theme.palette.type === "dark"
      ? "rgba(16, 185, 129, 0.18)"
      : "rgba(16, 185, 129, 0.12)",
    color: theme.palette.type === "dark" ? "#34d399" : "#059669",
    border: "1px solid rgba(16, 185, 129, 0.3)",
  },
  badgeOff: {
    background: theme.palette.type === "dark"
      ? "rgba(255, 255, 255, 0.04)"
      : "rgba(0, 0, 0, 0.04)",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.45)" : "#94a3b8",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.08)"
      : "1px solid rgba(0,0,0,0.06)",
  },
  textWrap: { display: "flex", flexDirection: "column", minWidth: 0 },
  title: {
    fontSize: "0.95rem",
    fontWeight: 600,
    color: theme.palette.type === "dark" ? "#f1f5f9" : "#1e293b",
  },
  status: { fontSize: "0.8rem", fontWeight: 500, marginTop: 2 },
  statusOn: { color: theme.palette.type === "dark" ? "#34d399" : "#059669" },
  statusOff: { color: theme.palette.type === "dark" ? "rgba(255,255,255,0.5)" : "#94a3b8" },
  desc: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1.5),
    fontSize: "0.82rem",
    lineHeight: 1.5,
  },
  primaryBtn: {
    borderRadius: 10,
    textTransform: "none",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  dangerBtn: {
    borderRadius: 10,
    textTransform: "none",
    fontWeight: 600,
    whiteSpace: "nowrap",
    color: theme.palette.type === "dark" ? "#fca5a5" : "#dc2626",
    borderColor: theme.palette.type === "dark"
      ? "rgba(220, 38, 38, 0.35)"
      : "rgba(220, 38, 38, 0.3)",
    "&:hover": {
      background: theme.palette.type === "dark"
        ? "rgba(220, 38, 38, 0.1)"
        : "rgba(220, 38, 38, 0.06)",
      borderColor: theme.palette.type === "dark" ? "#fca5a5" : "#dc2626",
    },
  },
  disablePanel: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 12,
    background: theme.palette.type === "dark"
      ? "rgba(220, 38, 38, 0.06)"
      : "rgba(220, 38, 38, 0.04)",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(220, 38, 38, 0.2)"
      : "1px solid rgba(220, 38, 38, 0.15)",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  disableRow: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    flexWrap: "wrap",
  },
}));

const MfaPersonalManager = () => {
  const classes = useStyles();
  const [me, setMe] = useState(null);
  // Antes de carregar (ou se falhar), não dá para dizer se a autenticação
  // está ligada: nada de "desativado" com o botão Ativar sem saber.
  const [loadState, setLoadState] = useState("loading");
  const [setupOpen, setSetupOpen] = useState(false);
  const [disableMode, setDisableMode] = useState(false);
  const [disableOtp, setDisableOtp] = useState("");
  const [disableLoading, setDisableLoading] = useState(false);

  const fetchMe = async () => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) throw new Error("Sessão sem usuário");
      const { data } = await api.get(`/users/${userId}`);
      setMe(data);
      setLoadState("ready");
    } catch (err) {
      toastError(err);
      setLoadState("error");
    }
  };

  const retryLoad = () => {
    setLoadState("loading");
    fetchMe();
  };

  useEffect(() => {
    fetchMe();
  }, []);

  const handleSetupClose = () => {
    setSetupOpen(false);
    fetchMe();
  };

  const handleDisable = async () => {
    if (disableOtp.length < 6) return;
    setDisableLoading(true);
    try {
      await api.post("/auth/mfa/disable", { otp: disableOtp });
      toast.success(i18n.t("mfa.profile.disable") + " ✓");
      setDisableMode(false);
      setDisableOtp("");
      fetchMe();
    } catch (err) {
      toastError(err);
    } finally {
      setDisableLoading(false);
    }
  };

  const enabled = !!me?.mfaEnabled;

  if (loadState === "loading") return <LoadingState />;
  if (loadState === "error") return <LoadError onRetry={retryLoad} />;

  return (
    <Paper className={classes.card} elevation={0}>
      <div className={classes.row}>
        <div className={classes.left}>
          <div className={`${classes.badge} ${enabled ? classes.badgeOn : classes.badgeOff}`}>
            {enabled ? (
              <ShieldCheck size={22} strokeWidth={1.75} />
            ) : (
              <ShieldOff size={22} strokeWidth={1.75} />
            )}
          </div>
          <div className={classes.textWrap}>
            <span className={classes.title}>{i18n.t("mfa.profile.menuItem")}</span>
            <span className={`${classes.status} ${enabled ? classes.statusOn : classes.statusOff}`}>
              {enabled
                ? me?.mfaEnabledAt
                  ? i18n.t("mfa.profile.statusEnabled", {
                      date: new Date(me.mfaEnabledAt).toLocaleDateString(),
                    })
                  : i18n.t("mfa.profile.statusEnabled", { date: "—" })
                : i18n.t("mfa.profile.statusDisabled")}
            </span>
          </div>
        </div>
        {!enabled && (
          <Button
            variant="contained"
            color="primary"
            className={classes.primaryBtn}
            startIcon={<ShieldCheck size={16} strokeWidth={2} />}
            onClick={() => setSetupOpen(true)}
          >
            {i18n.t("mfa.profile.enable")}
          </Button>
        )}
        {enabled && !disableMode && (
          <Button
            variant="outlined"
            className={classes.dangerBtn}
            startIcon={<ShieldOff size={16} strokeWidth={2} />}
            onClick={() => setDisableMode(true)}
          >
            {i18n.t("mfa.profile.disable")}
          </Button>
        )}
      </div>

      <Typography className={classes.desc}>
        {enabled
          ? i18n.t("mfa.profile.enabledDesc")
          : i18n.t("mfa.profile.disabledDesc")}
      </Typography>

      {enabled && disableMode && (
        <Box className={classes.disablePanel}>
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            <KeyRound size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />
            {i18n.t("mfa.profile.disableConfirm")}
          </Typography>
          <div className={classes.disableRow}>
            <TextField
              variant="outlined"
              size="small"
              value={disableOtp}
              onChange={(e) => setDisableOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              autoFocus
              disabled={disableLoading}
              inputProps={{
                inputMode: "numeric",
                maxLength: 6,
                style: { textAlign: "center", letterSpacing: 6, fontFamily: "monospace", fontSize: 16 },
              }}
              style={{ width: 140 }}
            />
            <Button
              variant="contained"
              className={classes.dangerBtn}
              onClick={handleDisable}
              disabled={disableLoading || disableOtp.length < 6}
            >
              {disableLoading ? <CircularProgress size={16} /> : i18n.t("mfa.profile.disable")}
            </Button>
            <Button
              size="small"
              startIcon={<X size={14} />}
              onClick={() => { setDisableMode(false); setDisableOtp(""); }}
              disabled={disableLoading}
            >
              {i18n.t("mfa.setup.cancel")}
            </Button>
          </div>
        </Box>
      )}

      <MfaSetupDialog open={setupOpen} onClose={handleSetupClose} />
    </Paper>
  );
};

export default MfaPersonalManager;
